
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Team } from '@/types/team';

export const useTeamData = (clubId?: string) => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchTeams = useCallback(async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('team_stats')
        .select('*');

      if (clubId) {
        query = query.eq('club_id', clubId);
      }

      const { data, error } = await query.order('name');
      if (error) throw error;

      // Transform the data to match the Team interface
      const transformedData = data?.map(team => ({
        id: team.id,
        name: team.name,
        club_id: team.club_id,
        season_id: team.season_id,
        division_id: team.division_id,
        birth_date_cutoff: team.birth_date_cutoff,
        created_at: team.created_at,
        profile_picture_url: team.profile_picture_url,
        player_count: team.player_count,
        clubs: team.club_name ? { 
          id: team.club_id, 
          name: team.club_name,
          organizations: null as any
        } : null,
        seasons: team.season_name ? {
          id: team.season_id,
          name: team.season_name,
          season_year: team.season_year,
          status: team.season_status
        } : null,
        divisions: team.division_name ? { 
          id: team.division_id, 
          name: team.division_name 
        } : null,
        team_coaches: [] as any[]
      })) || [];
      
      setTeams(transformedData);
    } catch (error) {
      console.error('Error fetching teams:', error);
      toast.error('Failed to load teams');
    } finally {
      setLoading(false);
    }
  }, [clubId]);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  const handleDelete = async (teamId: string) => {
    if (!confirm('Are you sure you want to delete this team?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('teams')
        .delete()
        .eq('id', teamId);

      if (error) throw error;
      toast.success('Team deleted successfully');
      fetchTeams();
    } catch (error) {
      console.error('Error deleting team:', error);
      toast.error('Failed to delete team');
    }
  };

  return {
    teams,
    loading,
    fetchTeams,
    handleDelete
  };
};
